/**
 * Format a date string to a readable format
 * @param {string} dateString - ISO date string (YYYY-MM-DD)
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return "N/A";

  // Create date at noon local time to avoid timezone issues
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day, 12, 0, 0);

  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * Format a time string from 24-hour to 12-hour format
 * @param {string} timeString - Time string in HH:MM format
 * @returns {string} Formatted time string
 */
export const formatTime = (timeString) => {
  if (!timeString) return "N/A";

  const [hours, minutes] = timeString.split(":");
  const hour = parseInt(hours);
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;

  return `${displayHour}:${minutes} ${period}`;
};

/**
 * Format squad information combining date and time
 * @param {string} squadDate - ISO date string (YYYY-MM-DD)
 * @param {string} squadTime - Time string in HH:MM format
 * @returns {string} Formatted squad info string
 */
export const formatSquadInfo = (squadDate, squadTime) => {
  if (!squadDate) return "N/A";

  const date = formatDate(squadDate);
  const time = formatTime(squadTime);

  return `${date} at ${time}`;
};

/**
 * Format squad information in a short form (e.g. "Sat - Jan 31 - 2:00 PM")
 * @param {string} squadDate - ISO date string (YYYY-MM-DD)
 * @param {string} squadTime - Time string in HH:MM format
 * @returns {string} Short formatted squad info string
 */
export const formatSquadInfoShort = (squadDate, squadTime) => {
  if (!squadDate) return "N/A";

  const [year, month, day] = squadDate.split("-").map(Number);
  const date = new Date(year, month - 1, day, 12, 0, 0);
  const dayOfWeek = date.toLocaleDateString("en-US", { weekday: "short" });
  const shortDate = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const time = formatTime(squadTime);

  return `${dayOfWeek} - ${shortDate} - ${time}`;
};

/**
 * Format a date to a short format (MM/DD/YYYY)
 * @param {string} dateString - ISO date string (YYYY-MM-DD) or ISO timestamp (YYYY-MM-DDTHH:mm:ss.sssZ)
 * @returns {string} Short formatted date string
 */
export const formatShortDate = (dateString) => {
  if (!dateString) return "N/A";

  try {
    // Handle ISO timestamp strings (e.g., "2024-10-24T10:30:00.000Z")
    if (dateString.includes("T")) {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleDateString("en-US", {
        month: "numeric",
        day: "numeric",
        year: "numeric",
      });
    }

    // Handle YYYY-MM-DD format (original behavior)
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day, 12, 0, 0);

    return date.toLocaleDateString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
    });
  } catch (error) {
    console.error("Error formatting date:", error, dateString);
    return "N/A";
  }
};

/**
 * Format a date to include time (MM/DD/YYYY HH:MM AM/PM)
 * @param {string} dateString - ISO date string (YYYY-MM-DD)
 * @returns {string} Formatted date with time string
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";

  // Create date at noon local time to avoid timezone issues
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day, 12, 0, 0);

  return date.toLocaleString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};
